// src/components/services/ServicesTable.jsx
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { Stack, useTheme } from "@mui/material";

import useGetServices from "../../hooks/services/useGetServices";
import useDeleteService from "../../hooks/services/useDeleteService";
import useDeleteAllServices from "../../hooks/services/useDeleteAllServices";
import useRestoreService from "../../hooks/services/useRestoreService";
import useRestoreAllServices from "../../hooks/services/useRestoreAllServices";
import useForceDeleteService from "../../hooks/services/useForceDeleteService";
import useForceDeleteAllServices from "../../hooks/services/useForceDeleteAllServices";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import ServicesTableRow from "./Table/ServicesTableRow";
import ServicesTableColumns from "./Table/ServicesTableColumns";
import ArchiveButton from "../global/Table/ArchiveButton";
import toast from "react-hot-toast";
import { usePermissions } from "../../auth/PermissionsContext";

const ServicesTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("services_list"),
            create: has("services_create"),
            edit: has("services_edit"),
            delete: has("services_delete"),
            forceDelete: has("services_force_delete"),
            archiveList: has("services_archive_list"),
            restore: has("services_restore"),
            restoreAll: has("services_restore-all-table"),
            deleteAll: has("services_delete-all-table"),
            massForceDeleteAll: has("services_mass_force_delete-all-table"),
        }),
        [has]
    );

    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const isArchive = location.pathname.includes("/services/archive");

    // CRUD Hooks
    const { deleteSer, isDelete } = useDeleteService();
    const { deleteAllSer, isDeleteAll } = useDeleteAllServices();
    const { restoreSer, isRestore } = useRestoreService();
    const { restoreAllSer, isRestoreAll } = useRestoreAllServices();
    const { forceDeleteSer, isForceDelete } = useForceDeleteService();
    const { forceDeleteAllSer, isForceDeleteAll } = useForceDeleteAllServices();

    const {
        page,
        perPage,
        search,
        sortBy,
        sortDir,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handlePageChange,
        handlePerPageChange,
        setSelectedIds,
    } = useTableActions({
        basePath: "/services",
        deleteSingle: deleteSer,
        deleteAll: deleteAllSer,
        restoreSingle: restoreSer,
        restoreAll: restoreAllSer,
        forceDeleteSingle: forceDeleteSer,
        forceDeleteAll: forceDeleteAllSer,
        isArchive,
    });

    // Local State (specific to this table)
    const [statusFilter, setStatusFilter] = React.useState("");

    const filters = useMemo(
        () => ({
            search: search || undefined,
            status: statusFilter || undefined,
            sort_by: sortBy,
            sort_dir: sortDir,
            ...(isArchive ? { only_trashed: true } : {}),
        }),
        [search, statusFilter, sortBy, sortDir, isArchive]
    );

    const filterConfig = useMemo(
        () => [
            {
                type: "select",
                name: "status",
                label: { ar: "الحالة", en: "Status" },
                options: [
                    { value: "", label: { ar: "الكل", en: "All" } },
                    { value: "active", label: { ar: "نشط", en: "Active" } },
                    { value: "disabled", label: { ar: "معطل", en: "Disabled" } },
                ],
                value: statusFilter,
                onChange: setStatusFilter,
            },
        ],
        [statusFilter]
    );

    const handleClearAllFilters = () => {
        setStatusFilter("");
    };

    const { isLoading, error, services, meta } = useGetServices(page, perPage, filters);

    const items = useMemo(() => services?.items || [], [services]);

    const rowMenuDisabled = isArchive
        ? !perms.restore && !perms.forceDelete
        : !perms.edit && !perms.delete;

    const renderRow = (params) => (
        <ServicesTableRow
            key={params.item.id}
            service={params.item}
            index={params.index}
            isDark={isDark}
            page={params.page}
            perPage={params.perPage}
            sortBy={params.sortBy}
            sortDir={params.sortDir}
            totalItems={items.length}
            selectedIds={selectedIds}
            onRowCheckboxClick={handleRowCheckboxClick}
            onMenuOpen={handleMenuOpen}
            isArchive={isArchive}
            menuDisabled={rowMenuDisabled}
        />
    );

    const bulkDeleteDisabled = isArchive ? !perms.massForceDeleteAll : !perms.deleteAll;

    const title = isArabic ? "الخدمات" : "Services";
    const subtitle = isArchive
        ? isArabic
            ? "عرض الخدمات المحذوفة (الأرشيف)"
            : "Archived services (soft deleted)"
        : isArabic
            ? "إدارة الخدمات المتاحة في النظام"
            : "Manage available services in the system";

    const addButtonDisabled = isArchive
        ? !perms.restoreAll || selectedIds.length === 0
        : !perms.create;

    const addButtonLabel = isArchive
        ? isRestoreAll
            ? isArabic
                ? "جاري الاستعادة..."
                : "Restoring..."
            : isArabic
                ? "استعادة المحدد"
                : "Restore selected"
        : isArabic
            ? "إضافة خدمة"
            : "Create new service";

    const addButtonAction = isArchive
        ? () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic ? "ليس لديك صلاحية للاستعادة" : "No restore permission"
                );
                return;
            }

            if (isRestoreAll) return;

            if (!selectedIds.length) {
                toast.error(
                    isArabic
                        ? "الرجاء تحديد العناصر المراد استرجاعها أولاً"
                        : "Please select items to restore first"
                );
                return;
            }

            restoreAllSer({ items: selectedIds });
            setSelectedIds([]);
        }
        : () => {
            if (addButtonDisabled) {
                toast.error(
                    isArabic ? "ليس لديك صلاحية للإنشاء" : "No create permission"
                );
                return;
            }

            navigate("/services/create");
        };

    return (
        <>
            {!isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/services/archive"
                        visible={perms.archiveList}
                        disabled={!perms.archiveList}
                        archiveLabelAr="الأرشيف"
                        archiveLabelEn="Archive"
                    />
                </Stack>
            )}
            {isArchive && (
                <Stack
                    direction="row"
                    justifyContent={isArabic ? "flex-start" : "flex-end"}
                    sx={{ mb: 1 }}
                >
                    <ArchiveButton
                        to="/services"
                        visible={true}
                        archiveLabelAr="العودة للقائمة"
                        archiveLabelEn="Back to list"
                    />
                </Stack>
            )}
            <TableLayout
                title={title}
                subtitle={subtitle}
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={addButtonLabel}
                onAddClick={addButtonAction}
                addButtonVisible={true}
                isLoading={isLoading}
                error={error}
                items={items}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                sortBy={sortBy}
                sortDir={sortDir}
                onRequestSort={handleRequestSort}
                columns={ServicesTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={handleMenuOpen}
                onMenuClose={handleMenuClose}
                onMenuDelete={handleMenuDelete}
                onMenuRestore={isArchive ? handleMenuRestore : undefined}
                onMenuEdit={!isArchive ? handleEdit : undefined}
                menuDisableEdit={!isArchive && !perms.edit}
                menuDisableRestore={isArchive && !perms.restore}
                menuDisableDelete={isArchive ? !perms.forceDelete : !perms.delete}
                isDeletingSingle={isArchive ? isRestore || isForceDelete : isDelete}
                selectedIds={selectedIds}
                onSelectAllClick={(e) => handleSelectAllClick(e, items)}
                onRowCheckboxClick={handleRowCheckboxClick}
                onBulkDelete={handleBulkDelete}
                bulkDeleteDisabled={bulkDeleteDisabled}
                isBulkDeleting={isArchive ? isForceDeleteAll : isDeleteAll}
                addButtonDisabled={addButtonDisabled}
                addButtonTooltip={
                    addButtonDisabled
                        ? isArabic
                            ? "لا تملك صلاحية تنفيذ هذا الإجراء"
                            : "You don't have permission"
                        : undefined
                }
                emptyLabelAr={isArchive ? "لا توجد خدمات في الأرشيف" : "لا توجد خدمات"}
                emptyLabelEn={isArchive ? "No archived services" : "No services"}
                loadErrorAr="حدث خطأ أثناء تحميل الخدمات"
                loadErrorEn="Failed to load services"
                filters={filterConfig}
                onClearAllFilters={handleClearAllFilters}
            />
        </>
    );
};

export default ServicesTable;
